import { useAppBridge } from '@shopify/app-bridge-react'
import {
  Banner,
  BlockStack,
  Box,
  Button,
  ButtonGroup,
  Card,
  Divider,
  FormLayout,
  Icon,
  InlineGrid,
  InlineStack,
  LegacyCard,
  LegacyStack,
  List,
  Modal,
  RadioButton,
  Select,
  SkeletonBodyText,
  Text,
} from '@shopify/polaris'
import { PlayIcon } from '@shopify/polaris-icons'
import { useFormikContext } from 'formik'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { CodeSnippet } from '../common/CodeSnippet'
import { ExtensionInfo, useCustomerPortalExtensionInfo } from '../common/customer-portal'
import {
  adminDeepLink,
  DialogActivator,
  ExternalLink,
  ShowIntercomButton,
  useDialogActivator,
  useFormikHandleChangeCallback,
  useShopFlag,
  useSystemData,
} from '../common/helpers'
import { useShopShopifySettingsSWR } from '../common/shopify-settings'
import { mainThemeId, ThemeSettings, useMainThemeSettingsSWR } from '../common/themes'
import { VideoModal } from '../common/VideoModal'
import { appEmbedInstallUrl } from '../dashboard-page/CustomerPortalStatus'
import { CustomerPortalSettings } from './api'
import { CustomColorsModal } from './CustomColorsModal'
import styles from './CustomerPortalThemeCard.module.css'
import { CustomScriptUrlModal } from './CustomScriptUrlModal'
import { CustomStylesModal } from './CustomStylesModal'

const customerAccountsInstallUrl = adminDeepLink(
  '/settings/checkout/editor?page=profile&context=apps'
)

const InstallCustomerPortalInstructionsCustomerAccounts = () => {
  return (
    <List>
      <List.Item>
        Navigate to{' '}
        <Button onClick={() => window.open(customerAccountsInstallUrl, '_blank')} variant="plain">
          checkout and customer accounts
        </Button>{' '}
        in your theme editor
      </List.Item>
      <List.Item>
        In the Theme Apps section, click the <b>+</b> symbol next to the{' '}
        <b>PayWhirl Customer Portal</b> app
      </List.Item>
      <List.Item>
        Click the <b>Add to menu</b> button in the blue box to create a link to the customer portal
        page
      </List.Item>
      <List.Item>
        Click <b>Save</b>
      </List.Item>
    </List>
  )
}

const InstallAppEmbedInstructionsLegacy = ({ themeId }: { themeId: string }) => {
  const shopify = useAppBridge()
  const installUrl = adminDeepLink(
    `/themes/${themeId}/editor?context=apps&template=index&activateAppId=${shopify.config.apiKey}/paywhirl-customer-portal`
  )

  return (
    <List>
      <List.Item>
        Navigate to your theme's{' '}
        <Button onClick={() => window.open(installUrl, '_blank')} variant="plain">
          app embeds section
        </Button>
      </List.Item>
      <List.Item>
        Enable <b>PayWhirl</b> (formerly <b>PayWhirl Customer Portal</b>) in the app embeds list
      </List.Item>
      <List.Item>
        Click on the <b>⌄</b> button to adjust relevant settings
      </List.Item>
      <List.Item>
        Click <b>Save</b>
      </List.Item>
    </List>
  )
}

export const InstallCustomerPortalModal = ({
  activator,
  legacy,
  themeSettings,
}: {
  activator: DialogActivator
  legacy: boolean
  themeSettings: ThemeSettings | undefined
}) => {
  const legacyVideoActivator = useDialogActivator()
  const customerAccountsVideoActivator = useDialogActivator()
  const extensionInfo = useCustomerPortalExtensionInfo()

  const title = legacy ? 'Install PayWhirl App Embed' : 'Install Customer Accounts Extension'

  useEffect(() => {
    const extensionInstalled = extensionInfo && extensionInfo.activations.length > 0
    if (!legacy && extensionInstalled) {
      activator.close()
      return
    }

    const appEmbedInstalled = themeSettings && themeSettings.customerPortalInstalled
    if (legacy && appEmbedInstalled) {
      activator.close()
    }
  }, [extensionInfo, themeSettings, legacy, activator])

  return (
    <Modal open={activator.open} onClose={activator.close} title={title}>
      {!legacy && (
        <>
          <Modal.Section>
            <InstallCustomerPortalInstructionsCustomerAccounts />
          </Modal.Section>
          <Modal.Section>
            <InlineStack blockAlign="center" align="space-between" gap="200">
              <InlineStack gap="200">
                <Button
                  onClick={customerAccountsVideoActivator.show}
                  icon={<Icon source={PlayIcon} tone="base" />}
                >
                  Watch video
                </Button>
                <ShowIntercomButton message="I'd like help setting up PayWhirl in Shopify customer accounts">
                  Chat with us
                </ShowIntercomButton>
              </InlineStack>
              <InlineStack gap="200">
                <Button onClick={activator.close}>Close</Button>
                <Button
                  onClick={() => window.open(customerAccountsInstallUrl, '_blank')}
                  variant="primary"
                >
                  Install now
                </Button>
              </InlineStack>
            </InlineStack>
          </Modal.Section>
          <VideoModal
            src="https://www.youtube.com/embed/GezzssBuX3w"
            title="How To Install The PayWhirl Customer Portal"
            activator={customerAccountsVideoActivator}
          />
        </>
      )}
      {legacy && (
        <>
          <Modal.Section>
            <InstallAppEmbedInstructionsLegacy themeId={mainThemeId(themeSettings)} />
          </Modal.Section>
          <Modal.Section>
            <InlineStack blockAlign="center" align="space-between" gap="200">
              <InlineStack gap="200">
                <Button
                  onClick={legacyVideoActivator.show}
                  icon={<Icon source={PlayIcon} tone="base" />}
                >
                  Watch video
                </Button>
                <ShowIntercomButton message="I'd like help setting up PayWhirl App Embed in Shopify theme">
                  Chat with us
                </ShowIntercomButton>
              </InlineStack>
              <InlineStack gap="200">
                <Button onClick={activator.close}>Close</Button>
                <Button
                  onClick={() =>
                    window.open(
                      appEmbedInstallUrl(mainThemeId(themeSettings), shopify.config.apiKey),
                      '_blank'
                    )
                  }
                  variant="primary"
                >
                  Install now
                </Button>
              </InlineStack>
            </InlineStack>
          </Modal.Section>
          <VideoModal
            src="https://www.youtube.com/embed/iw0pCjDUnDA?start=61"
            title="How To Setup Your Shopify Theme"
            activator={legacyVideoActivator}
          />
        </>
      )}
    </Modal>
  )
}

export const AppEmbedStatusBanner = ({
  onInstall,
  mainThemeSettings,
}: {
  onInstall: () => void
  mainThemeSettings: ThemeSettings | undefined
}) => {
  if (!mainThemeSettings) {
    return null
  }

  const installed = mainThemeSettings.customerPortalInstalled

  return (
    <>
      {installed && (
        <Banner
          tone="success"
          action={{
            content: 'Edit settings',
            onAction: () =>
              window.open(
                adminDeepLink(`/themes/${mainThemeId(mainThemeSettings)}/editor?context=apps`),
                '_blank'
              ),
          }}
        >
          <Text as="p">App Embed successfully installed in the main theme</Text>
        </Banner>
      )}
      {!mainThemeSettings.customerPortalInstalled && (
        <Banner
          tone="warning"
          action={{
            content: 'Install now',
            onAction: onInstall,
          }}
        >
          <Text as="p">App Embed not installed</Text>
        </Banner>
      )}
    </>
  )
}

export const CustomerPortalExtensionStatusBanner = ({
  onInstall,
  extensionInfo,
}: {
  onInstall: () => void
  extensionInfo?: ExtensionInfo
}) => {
  if (!extensionInfo) {
    return null
  }

  const installed = extensionInfo.activations.length > 0

  if (installed) {
    return (
      <Banner
        tone="success"
        action={{
          content: 'Edit settings',
          onAction: () => window.open(customerAccountsInstallUrl, '_blank'),
        }}
      >
        <Text as="p">Customer Accounts Extension successfully installed</Text>
      </Banner>
    )
  }

  return (
    <Banner
      tone="warning"
      action={{
        content: 'Install now',
        onAction: onInstall,
      }}
    >
      <Text as="p">Customer Accounts Extension not installed</Text>
    </Banner>
  )
}

export const BoxRadioButton = ({
  id,
  name,
  label,
  checked,
  children,
  onChange,
}: {
  id: string
  name: string
  label: React.ReactNode
  checked: boolean
  children: React.ReactNode
  onChange: () => void
}) => {
  const [hover, setHover] = useState(false)

  return (
    <label
      htmlFor={id}
      className={styles.boxLabel}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <Box
        borderColor={checked ? 'input-border-active' : hover ? 'border-hover' : 'border'}
        borderWidth="025"
        borderRadius="200"
        padding="200"
        minHeight="100%"
      >
        <BlockStack gap="200">
          <RadioButton label={label} checked={checked} name={name} id={id} onChange={onChange} />
          {children}
        </BlockStack>
      </Box>
    </label>
  )
}

type CustomerPortalVersion = 'customer-accounts' | 'app-embed' | 'standalone'

export const CustomerPortalThemeCard = () => {
  const customStylesActivator = useDialogActivator()
  const customScriptUrlActivator = useDialogActivator()
  const customColorsActivator = useDialogActivator()
  const installCustomerPortalActivator = useDialogActivator()
  const customScriptUrlFlagEnabled = useShopFlag('enable_customer_portal_custom_script_url')
  const extensionInfo = useCustomerPortalExtensionInfo()

  const systemData = useSystemData()
  const shopifySettings = useShopShopifySettingsSWR()
  const { data: mainThemeSettings } = useMainThemeSettingsSWR()

  const formik = useFormikContext<CustomerPortalSettings>()
  const handleChange = useFormikHandleChangeCallback(formik.setFieldValue)

  const standaloneEnabled =
    formik.values.standalone_customer_portal ||
    shopifySettings.data?.shopifyNewCustomerAccountsEnabled

  const customerPortalExtensionEnabled = formik.values.customer_portal_extension_enabled

  const handleCustomerPortalVersionChange = useCallback(
    (newCustomerPortalVersion: CustomerPortalVersion) => {
      if (newCustomerPortalVersion === 'customer-accounts') {
        handleChange(true, 'customer_portal_extension_enabled')
        handleChange(false, 'standalone_customer_portal')
      }

      if (newCustomerPortalVersion === 'app-embed') {
        handleChange(false, 'customer_portal_extension_enabled')
        handleChange(false, 'standalone_customer_portal')
      }

      if (newCustomerPortalVersion === 'standalone') {
        handleChange(false, 'customer_portal_extension_enabled')
        handleChange(true, 'standalone_customer_portal')
      }
    },
    [handleChange]
  )

  const customerPortalVersion = useMemo(() => {
    if (
      formik.values.customer_portal_extension_enabled &&
      formik.values.standalone_customer_portal
    ) {
      return 'standalone'
    }

    if (formik.values.customer_portal_extension_enabled) {
      return 'customer-accounts'
    }

    if (formik.values.standalone_customer_portal) {
      return 'standalone'
    }

    return 'app-embed'
  }, [formik.values.customer_portal_extension_enabled, formik.values.standalone_customer_portal])

  if (!shopifySettings.data) {
    return (
      <Card>
        <BlockStack gap="800">
          <SkeletonBodyText lines={1} />
          <SkeletonBodyText lines={8} />
        </BlockStack>
      </Card>
    )
  }

  return (
    <>
      <LegacyCard>
        <BlockStack gap="100">
          <LegacyCard.Section>
            <BlockStack gap="400">
              <FormLayout>
                <BlockStack gap="400">
                  <Text variant="bodyMd" as="p">
                    PayWhirl’s customer portal supports both Shopify customer account types. Your
                    store is currently using Shopify's{' '}
                    <Text as="strong">
                      {shopifySettings.data?.shopifyNewCustomerAccountsEnabled
                        ? 'New Customer Accounts'
                        : 'Legacy Customer Accounts'}
                    </Text>
                    . Select the option that best suits your store.
                  </Text>
                  <InlineGrid gap="200" columns={{ xs: 1, sm: 3 }}>
                    <BoxRadioButton
                      label={<Text as="span">Customer Accounts Extension</Text>}
                      checked={customerPortalVersion === 'customer-accounts'}
                      onChange={() => handleCustomerPortalVersionChange('customer-accounts')}
                      name="customer_portal_version"
                      id="customer_portal_version_customer-accounts"
                    >
                      <BlockStack gap="200">
                        <Text as="span" tone="subdued">
                          Integrated with Shopify's New Customer Accounts
                        </Text>
                      </BlockStack>
                    </BoxRadioButton>
                    <BoxRadioButton
                      label={<Text as="span">Embedded</Text>}
                      checked={customerPortalVersion === 'app-embed'}
                      onChange={() => handleCustomerPortalVersionChange('app-embed')}
                      name="customer_portal_version"
                      id="customer_portal_version_app-embed"
                    >
                      <BlockStack gap="200">
                        <Text as="span" tone="subdued">
                          Integrated with Shopify's Legacy Customer Accounts using an App Embed
                        </Text>
                      </BlockStack>
                    </BoxRadioButton>
                    <BoxRadioButton
                      label={<Text as="span">Standalone</Text>}
                      checked={customerPortalVersion === 'standalone'}
                      onChange={() => handleCustomerPortalVersionChange('standalone')}
                      name="customer_portal_version"
                      id="customer_portal_version_standalone"
                    >
                      <BlockStack gap="200">
                        <Text as="span" tone="subdued">
                          Displayed on an external URL, outside of your Shopify store
                        </Text>
                      </BlockStack>
                    </BoxRadioButton>
                  </InlineGrid>
                </BlockStack>
                {customerPortalVersion === 'customer-accounts' && (
                  <>
                    {shopifySettings.data &&
                      !shopifySettings.data.shopifyNewCustomerAccountsEnabled && (
                        <Banner tone="warning">
                          <Text as="p">
                            Since your store is using Shopify's Legacy Customer Accounts, the
                            Customer Accounts Extension is not accessible. Switch to the Embedded
                            version, or enable New Customer Accounts in Shopify{' '}
                            <ExternalLink href={adminDeepLink('/settings/customer_accounts')}>
                              here
                            </ExternalLink>
                          </Text>
                        </Banner>
                      )}
                    {shopifySettings.data?.shopifyNewCustomerAccountsEnabled && (
                      <CustomerPortalExtensionStatusBanner
                        onInstall={installCustomerPortalActivator.show}
                        extensionInfo={extensionInfo}
                      />
                    )}
                  </>
                )}
                {customerPortalVersion === 'app-embed' && (
                  <>
                    {!!shopifySettings.data?.shopifyNewCustomerAccountsEnabled && (
                      <Banner tone="warning">
                        <Text as="p">
                          Since your store is using Shopify's New Customer Accounts, the Embedded
                          version is not available. Use the Customer Accounts Extension, or switch
                          to Legacy Customer Accounts in Shopify{' '}
                          <ExternalLink href={adminDeepLink('/settings/customer_accounts')}>
                            here
                          </ExternalLink>
                        </Text>
                      </Banner>
                    )}
                    {!shopifySettings.data?.shopifyNewCustomerAccountsEnabled && (
                      <AppEmbedStatusBanner
                        onInstall={installCustomerPortalActivator.show}
                        mainThemeSettings={mainThemeSettings}
                      />
                    )}
                  </>
                )}
              </FormLayout>
              {customerPortalVersion !== 'customer-accounts' && (
                <>
                  {customerPortalVersion === 'app-embed' && (
                    <FormLayout>
                      <Select
                        id="customer_portal_theme"
                        name="customer_portal_theme"
                        label="Theme"
                        options={[
                          { label: 'Classic', value: 'classic' },
                          { label: 'Modern', value: 'modern' },
                        ]}
                        value={standaloneEnabled ? 'modern' : formik.values.customer_portal_theme}
                        onChange={handleChange}
                        disabled={standaloneEnabled}
                      />
                    </FormLayout>
                  )}
                  <ButtonGroup>
                    {(standaloneEnabled || formik.values.customer_portal_theme === 'modern') && (
                      <Button onClick={customColorsActivator.show}>Customize</Button>
                    )}
                    <Button onClick={customStylesActivator.show} variant="plain">
                      Add custom CSS rules
                    </Button>
                    {customScriptUrlFlagEnabled && (
                      <Button onClick={customScriptUrlActivator.show} variant="plain">
                        Add custom Script URL
                      </Button>
                    )}
                  </ButtonGroup>
                </>
              )}
            </BlockStack>
          </LegacyCard.Section>
          <Divider />
          <LegacyCard.Section>
            <LegacyStack vertical spacing="tight">
              <Text as="p">
                Add or share this URL anywhere you'd like to give customers access to the customer
                portal.
              </Text>
              <CodeSnippet code={systemData?.customertPortalUrl ?? ''} showCopyButton={true} />
            </LegacyStack>
          </LegacyCard.Section>
        </BlockStack>
      </LegacyCard>
      <CustomStylesModal activator={customStylesActivator} />
      <CustomScriptUrlModal activator={customScriptUrlActivator} />
      <CustomColorsModal activator={customColorsActivator} />
      <InstallCustomerPortalModal
        activator={installCustomerPortalActivator}
        legacy={!customerPortalExtensionEnabled}
        themeSettings={mainThemeSettings}
      />
    </>
  )
}
